// @flow

import React, { useState, useEffect, SyntheticEvent } from 'react';
import { Helmet } from 'react-helmet';
import { useHistory } from 'react-router-dom';
import moment from 'moment';
import BasicHeader from './BasicHeader';
import PlanPickerStep, { getActualMonthlyPrice, getActualYearlyPrice } from './PlanPickerStep';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import StripeElementWrapper from './StripeElementWrapper';
import { useSelector, useDispatch } from 'react-redux';
import { selectUser } from '../../reduxStore/modules/user/selectors';
import { log } from '../../utils/jsUtils';
import adTracking from '../../services/adTracking';
import { calculateTieredPrice, getCurrentTierPrice, getTierBreakdown } from '../../utils/pricingUtils';
import type { TierBreakdown } from '../../utils/pricingUtils';
import { hasTrial, getTrialDays, formatTrialEndDate, getTrialCTAText, getDueToday, buildTrialPayload } from '../../utils/trialUtils';
import SPBackend from '../../services/SPBackend';
import { setSnackMessage } from '../../reduxStore/modules/common/actions';
import { setBilling } from '../../reduxStore/modules/billing/actions';
import useOnboardingRedirect from '../../hooks/useOnboardingRedirect';
import { getNextOnboardingStep } from '../../utils/onboardingUtils';

import OutlinedInput from '@mui/material/OutlinedInput';
import FormControl from '@mui/material/FormControl';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import IconButton from '@mui/material/IconButton';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepButton from '@mui/material/StepButton';

import Logo from '../../assets/askneo-favicon.png';
import ContentContainer from '../SharedComponents/ContentContainer';

// The old styles
import { createClasses } from '../../utils/stylesUtils';
import signupStyles from '../PublicCpts/Styles/Signup.Style';
import styles from './Styles/CheckoutHome.Style';

// Build the classes from classes:
import { useTheme } from '@mui/material/styles';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_KEY);

// Step labels for the 3-step flow
const STEP_LABELS = ['Choose Plan', 'Customize', 'Get Started'];

// Helper to get tier savings message
// For flat-amount tiers: shows how many more seats can be added at same price
// For unit-based tiers: compares per-seat price with next tier
const getTierSavingsMessage = (tiers: ?Array<Object>, seatCount: number): ?string => {
  if (!tiers || tiers.length < 2 || seatCount <= 0) return null;

  for (let i = 0; i < tiers.length - 1; i++) {
    const tier = tiers[i];
    if (tier.up_to && seatCount <= tier.up_to) {
      const isFlatAmountTier = (tier.flat_amount || 0) > 0 && (tier.unit_amount || 0) === 0;
      const seatsRemainingInTier = tier.up_to - seatCount;

      // For flat-amount tiers: show how many more seats they can add at same price
      if (isFlatAmountTier && seatsRemainingInTier > 0) {
        return `Add up to ${seatsRemainingInTier} more seat${seatsRemainingInTier > 1 ? 's' : ''} at the same price`;
      }

      // At tier boundary or unit-based tier: show per-seat savings for next tier
      const nextTier = tiers[i + 1];
      const seatsNeeded = tier.up_to + 1 - seatCount;
      const nextTierMinSeats = tier.up_to + 1;

      // Helper to calculate effective per-seat price
      const getEffectivePerSeatPrice = (t: Object, seats: number): number => {
        const flatAmount = t.flat_amount || 0;
        const unitAmount = t.unit_amount || 0;
        if (flatAmount > 0 && unitAmount === 0) {
          return flatAmount / seats;
        }
        if (flatAmount > 0 && unitAmount > 0) {
          return (flatAmount / seats) + unitAmount;
        }
        return unitAmount;
      };

      const currentPrice = getEffectivePerSeatPrice(tier, seatCount) / 100;
      const nextPrice = getEffectivePerSeatPrice(nextTier, nextTierMinSeats) / 100;
      const savings = currentPrice - nextPrice;

      if (savings > 0) {
        return `Add ${seatsNeeded} more seat${seatsNeeded > 1 ? 's' : ''} to save $${savings.toFixed(2)}/seat`;
      }

      return null;
    }
  }
  return null;
};

// Helper to get total price for a plan (handles tiered vs flat pricing)
const getEffectiveTotalPrice = (plan: ?Object, seatCount: number): number => {
  if (!plan) return 0;
  if (plan.billing_scheme === 'tiered' && plan.tiers) {
    return calculateTieredPrice(plan.tiers, seatCount, plan.tiers_mode);
  }
  // Flat rate pricing
  return ((plan.unit_amount || plan.amount || 0) * seatCount) / 100;
};

// Helper to calculate yearly savings percentage based on current seat selection
const calculateYearlySavings = (monthlyPlan: ?Object, yearlyPlan: ?Object, seatCount: number): ?number => {
  if (!monthlyPlan || !yearlyPlan) return null;

  // Get total cost for current seat count at monthly rate (for 12 months)
  const monthlyTotal = getEffectiveTotalPrice(monthlyPlan, seatCount);
  const twelveMonthsCost = monthlyTotal * 12;

  // Get total cost for current seat count at yearly rate
  const yearlyTotal = getEffectiveTotalPrice(yearlyPlan, seatCount);

  if (twelveMonthsCost === 0) return null;

  // Calculate savings percentage
  const savingsPercent = ((twelveMonthsCost - yearlyTotal) / twelveMonthsCost) * 100;

  return savingsPercent > 0 ? Math.round(savingsPercent) : null;
};

// Subcomponent for Step 1: Configure Seats
function ChooseSubscriptionStep(props: {
  monthlyPlanInfo: ?Object,
  yearlyPlanInfo: ?Object,
  productInfo: ?Object,
  seatCount: number,
  billingCycle: string,
  onSeatCountChange: Function,
  onBillingCycleChange: Function,
  yearlySavings: ?number,
  onContinue: Function,
  trialDays: ?number,
  trialEndDate: string,
}) {
  const {
    monthlyPlanInfo,
    yearlyPlanInfo,
    productInfo,
    seatCount,
    billingCycle,
    onSeatCountChange,
    onBillingCycleChange,
    yearlySavings,
    onContinue,
    trialDays,
    trialEndDate
  } = props;

  const theme = useTheme();
  const classes = createClasses({
    ...styles,
    ...signupStyles
  }, theme);

  const isTrialPlan = trialDays !== null && trialDays > 0;

  // Get the active plan based on billing cycle
  const activePlan = billingCycle === 'year' ? yearlyPlanInfo : monthlyPlanInfo;

  // Calculate prices using the correct plan for each cycle
  const getMonthlyPrice = () => {
    if (!monthlyPlanInfo) return 0;
    return getEffectiveTotalPrice(monthlyPlanInfo, seatCount);
  };

  const getYearlyPrice = () => {
    if (!yearlyPlanInfo) return 0;
    return getEffectiveTotalPrice(yearlyPlanInfo, seatCount);
  };

  // Get per-seat price display for tiered or flat pricing
  const getPerSeatPrice = (plan: ?Object) => {
    if (!plan) return 0;
    if (plan.billing_scheme === 'tiered' && plan.tiers) {
      return getCurrentTierPrice(plan.tiers, seatCount);
    }
    const amount = plan.unit_amount || plan.amount || 0;
    return amount / 100;
  };

  // Get tier savings message if applicable (for the active plan)
  const tierSavingsMessage = activePlan?.billing_scheme === 'tiered'
    ? getTierSavingsMessage(activePlan.tiers, seatCount)
    : null;

  // Calculate total price based on selected billing cycle
  const calculateTotalPrice = () => {
    if (billingCycle === 'year') {
      return getYearlyPrice();
    }
    return getMonthlyPrice();
  };

  const totalPrice = calculateTotalPrice();

  return (
    <Box>
      <Helmet>
        <title>Neo | Configure Your Subscription</title>
      </Helmet>
      <Grid container>
        {/* Trial Banner */}
        {isTrialPlan && (
          <Grid item xs={12} sx={{ mb: 2 }}>
            <Alert severity="info" data-testid="trial-banner">
              <Typography variant="body2">
                {"You're starting a "}
                <strong>{`${trialDays}-day free trial`}</strong>
                {" — no charge until "}
                {trialEndDate}
              </Typography>
            </Alert>
          </Grid>
        )}

        {/* Seat Selection */}
        <Grid item xs={12}>
          <Box sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            p: 2.5,
            mb: 2,
            backgroundColor: 'action.hover',
          }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
              {'Team size'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              {'How many team members need access?'}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <IconButton
                onClick={() => onSeatCountChange(Math.max(1, seatCount - 1))}
                disabled={seatCount <= 1}
              >
                <RemoveIcon />
              </IconButton>
              <Typography variant="h4" sx={{ marginX: 2, minWidth: '50px', textAlign: 'center', fontWeight: 600 }}>
                {seatCount}
              </Typography>
              <IconButton
                onClick={() => onSeatCountChange(seatCount + 1)}
              >
                <AddIcon />
              </IconButton>
              <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                {seatCount === 1 ? 'seat' : 'seats'}
              </Typography>
            </Box>
          </Box>
        </Grid>

        {/* Billing Cycle Selection - only show if both plans are available */}
        {(monthlyPlanInfo && yearlyPlanInfo) && (
          <Grid item xs={12}>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, mb: 0.5 }}>
              {'Billing cycle'}
            </Typography>
            <RadioGroup
              value={billingCycle}
              onChange={(e) => onBillingCycleChange(e.target.value)}
              sx={{ marginBottom: 1 }}
            >
              {monthlyPlanInfo && (
                <FormControlLabel
                  value="month"
                  control={<Radio size="small" />}
                  label={`Monthly - $${getMonthlyPrice().toFixed(2)}/month${getPerSeatPrice(monthlyPlanInfo) !== null ? ` ($${getPerSeatPrice(monthlyPlanInfo).toFixed(2)}/seat)` : ''}`}
                />
              )}
              {yearlyPlanInfo && (
                <FormControlLabel
                  value="year"
                  control={<Radio size="small" />}
                  label={`Yearly - $${getYearlyPrice().toFixed(2)}/year${getPerSeatPrice(yearlyPlanInfo) !== null ? ` ($${getPerSeatPrice(yearlyPlanInfo).toFixed(2)}/seat)` : ''}${yearlySavings ? ` - Save ${yearlySavings}%` : ''}`}
                />
              )}
            </RadioGroup>
          </Grid>
        )}

        {/* Tier savings message - show independently of billing cycle */}
        {tierSavingsMessage && (
          <Grid item xs={12}>
            <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 500, mb: 2 }}>
              {tierSavingsMessage}
            </Typography>
          </Grid>
        )}

        {/* Trial price summary */}
        {isTrialPlan && (
          <Grid item xs={12} sx={{ mb: 1 }}>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              {'$0.00 due today'}
              <Typography component="span" variant="body2" color="text.secondary">
                {` — then $${totalPrice.toFixed(2)}/${billingCycle === 'year' ? 'year' : 'mo'} starting ${trialEndDate}`}
              </Typography>
            </Typography>
          </Grid>
        )}

        {/* Continue CTA */}
        <Grid item xs={12}>
          <Button
            color="primary"
            disabled={!activePlan?.id}
            sx={classes.primaryCta}
            onClick={onContinue}
            variant="contained"
            fullWidth
          >
            {isTrialPlan ? 'Continue to payment' : 'Continue to Checkout'}
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
}

// Subcomponent for Step 2: Review & Pay (handles payment)
function ReviewAndPayStep(props: {
  monthlyPlanInfo: ?Object,
  yearlyPlanInfo: ?Object,
  productInfo: ?Object,
  seatCount: number,
  billingCycle: string,
  yearlySavings: ?number,
  onBack: Function,
  trialDays: ?number,
  trialEndDate: string,
}) {
  const {
    monthlyPlanInfo,
    yearlyPlanInfo,
    productInfo,
    seatCount,
    billingCycle,
    yearlySavings,
    onBack,
    trialDays,
    trialEndDate
  } = props;

  const isTrialPlan = trialDays !== null && trialDays > 0;

  // Get the active plan based on billing cycle
  const activePlan = billingCycle === 'year' ? yearlyPlanInfo : monthlyPlanInfo;

  // Use the useStripe and useElements hooks provided by @stripe/react-stripe-js
  const stripe = useStripe();
  const elements = useElements();

  const history = useHistory();
  const dispatch = useDispatch();
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [cardholderName, setCardholderName] = useState('');
  const [chargeError, setChargeError] = useState('');

  // Use memoized selector to avoid unnecessary rerenders
  const user = useSelector(selectUser);

  const theme = useTheme();
  const classes = createClasses({
    ...styles,
    ...signupStyles
  }, theme);

  // Calculate total price based on selected billing cycle
  const totalPrice = getEffectiveTotalPrice(activePlan, seatCount);
  const dueToday = isTrialPlan ? 0 : totalPrice;

  const handleSubmit = async (ev: SyntheticEvent<HTMLFormElement>) => {
    try {
      ev.preventDefault();
      setPaymentLoading(true);

      // First check if stripe and elements are loaded
      if (!stripe || !elements) {
        setChargeError('Stripe is still loading. Please try again in a moment.');
        setPaymentLoading(false);
        return;
      }

      // Get the correct plan ID based on billing cycle
      const selectedPlanId = billingCycle === 'year'
        ? yearlyPlanInfo?.id
        : monthlyPlanInfo?.id;

      if (!selectedPlanId) {
        setChargeError('Selected plan is not available. Please try another option.');
        setPaymentLoading(false);
        return;
      }

      log('submit payment!', ev);
      const cardElement = elements.getElement(CardElement);

      if (!cardElement) {
        setChargeError('Card information not found. Please refresh and try again.');
        setPaymentLoading(false);
        return;
      }

      log('[handleSubmit] selectedPlanId:', selectedPlanId);
      log('[handleSubmit] seatCount:', seatCount);
      log('[handleSubmit] billingCycle:', billingCycle);

      const pmResult = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
        billing_details: {
          name: cardholderName
        }
      });
      const { paymentMethod, error: pmError } = pmResult;
      if (pmError) {
        log('Stripe paymentMethod error: ', pmError);
        if (pmError.code === 'parameter_invalid_empty') {
          throw Error('All fields are required.');
        }
        throw Error(pmError.message);
      }

      log('resulting pmResult: ', paymentMethod);

      // Build trial payload using trialUtils (sends trialDays integer, not timestamp)
      const trialPayload = buildTrialPayload(productInfo);

      const paymentPayload = {
        paymentMethod,
        planId: selectedPlanId,
        quantity: seatCount,
        ...trialPayload
      };

      SPBackend.chargePlan(paymentPayload, user)
        .then((response) => {
          setChargeError('');
          log('charge response: ', response);

          // Extract invoice URL if available from backend response
          const invoiceUrl = response.data?.invoice?.hosted_url;

          SPBackend.getAccount(user)
            .then((resp) => {
              log('[getAccount] response: ', resp);
              const { data } = resp;
              const { billing: newBilling } = data;

              // Update Redux billing state
              dispatch(setBilling(newBilling));

              // Show success notification with invoice link
              const successMessage = isTrialPlan
                ? 'Free trial started! Setting up your phone number...'
                : invoiceUrl
                  ? 'Subscription successful! Click to view your invoice.'
                  : 'Subscription successful! Setting up your phone number...';

              dispatch(setSnackMessage({
                message: successMessage,
                level: 'success',
                duration: 8000,
                action: invoiceUrl ? {
                  label: 'View Invoice',
                  onClick: () => window.open(invoiceUrl, '_blank')
                } : null
              }));

              // Track conversion: trial start vs direct purchase
              const purchasePrice = billingCycle === 'year'
                ? yearlyPlanInfo : monthlyPlanInfo;
              if (isTrialPlan) {
                adTracking.trackCustomEvent('StartTrial', {
                  value: 0,
                  currency: 'USD',
                  content_name: productInfo?.name,
                  predicted_ltv: purchasePrice ? (purchasePrice.unit_amount / 100) * seatCount : 0
                });
                adTracking.trackGoogleConversion('StartTrial', { value: 0, currency: 'USD' });
              } else {
                adTracking.trackEvent('Purchase', {
                  value: purchasePrice ? (purchasePrice.unit_amount / 100) * seatCount : 0,
                  currency: 'USD',
                  content_name: productInfo?.name
                });
                adTracking.trackGoogleConversion('Purchase', {
                  value: purchasePrice ? (purchasePrice.unit_amount / 100) * seatCount : 0,
                  currency: 'USD'
                });
              }

              // After successful subscription, redirect to next onboarding step
              const nextStep = getNextOnboardingStep('/subscribe');
              log('redirecting to', nextStep, 'after subscription');
              history.push(nextStep);
              setPaymentLoading(false);
            })
            .catch((err) => {
              log('[handleSubmit] getAccount error: ', err);
              setPaymentLoading(false);
            });
        })
        .catch((error) => {
          log('[handleSubmit] charge error.response: ', error.response);
          setPaymentLoading(false);

          let errorMessage = 'Payment failed. Please try again.';

          if (error.response?.data) {
            const { data, status } = error.response;
            if (typeof data === 'string') {
              errorMessage = data;
            } else if (data.message) {
              errorMessage = data.message;
            } else if (data.error) {
              errorMessage = typeof data.error === 'string' ? data.error : data.error.message;
            }

            // Add status code context for user
            if (status === 400) {
              errorMessage = `Request error: ${errorMessage}`;
            } else if (status === 402) {
              errorMessage = `Payment declined: ${errorMessage}`;
            } else if (status >= 500) {
              errorMessage = 'Server error. Please try again later.';
            }
          } else if (error.message) {
            errorMessage = error.message;
          }

          setChargeError(errorMessage);
        });
    } catch (e) {
      log('[handleSubmit] e.name: ', e.name);
      if (e.name === 'IntegrationError') {
        setChargeError('Missing card information');
      } else {
        log('[handleSubmit] error caught: ', e.name, e);
        setChargeError(e.message);
      }
      setPaymentLoading(false);
    }
  };

  // Get per-seat price for display
  const getPerSeatPrice = () => {
    if (!activePlan) return null;
    if (activePlan.billing_scheme === 'tiered' && activePlan.tiers) {
      return getCurrentTierPrice(activePlan.tiers, seatCount);
    }
    const amount = activePlan.unit_amount || activePlan.amount || 0;
    return amount / 100;
  };

  const perSeatPrice = getPerSeatPrice();

  // Get tier breakdown for graduated pricing (shows multiple tiers if applicable)
  const tierBreakdown = activePlan?.billing_scheme === 'tiered' && activePlan?.tiers
    ? getTierBreakdown(activePlan.tiers, seatCount, activePlan.tiers_mode)
    : null;

  const billingInterval = billingCycle === 'year' ? 'year' : 'month';

  return (
    <form onSubmit={handleSubmit}>
      <Helmet>
        <title>Neo | Review & Confirm</title>
      </Helmet>
      <Grid container>
        {/* Enhanced Order Summary - primary visual element in Step 2 */}
        <Grid item xs={12}>
          <Box sx={classes.orderSummaryBox}>
            {/* Trial: Due Today $0.00 */}
            {isTrialPlan ? (
              <>
                <Box sx={classes.summaryRow}>
                  <Typography sx={classes.orderSummaryTitle}>
                    {'Due today'}
                  </Typography>
                  <Typography sx={{ ...classes.orderSummaryPrice, color: 'success.main' }} data-testid="due-today">
                    {'$0.00'}
                  </Typography>
                </Box>
                <Box sx={classes.summaryRow}>
                  <Typography variant="body1" color="text.secondary">
                    {`Your ${trialDays}-day free trial ends on `}
                    <strong>{trialEndDate}</strong>
                  </Typography>
                </Box>
                <Box sx={classes.summaryRow}>
                  <Typography variant="body1" color="text.secondary">
                    {'First charge: '}
                    <strong>{`$${totalPrice.toFixed(2)}`}</strong>
                    {' on '}
                    <strong>{trialEndDate}</strong>
                    {` (billed ${billingCycle === 'year' ? 'yearly' : 'monthly'})`}
                  </Typography>
                </Box>
              </>
            ) : (
              <Box sx={classes.summaryRow}>
                <Typography sx={classes.orderSummaryTitle}>
                  {productInfo?.name || 'Subscription'}
                </Typography>
                <Typography sx={classes.orderSummaryPrice}>
                  {billingCycle === 'year'
                    ? `$${totalPrice.toFixed(2)}/year`
                    : `$${totalPrice.toFixed(2)}/month`
                  }
                </Typography>
              </Box>
            )}

            {!isTrialPlan && (
              <Box sx={classes.summaryRow}>
                <Typography variant="body1" color="text.secondary">
                  {billingCycle === 'year' ? 'Billed annually' : 'Billed monthly'}
                </Typography>
              </Box>
            )}

            {/* Seat pricing breakdown */}
            {tierBreakdown && tierBreakdown.length > 1 ? (
              // Multi-line breakdown for graduated pricing
              <>
                {tierBreakdown.map((tier, index) => (
                  <Box key={index} sx={classes.summaryRow}>
                    <Typography variant="body1" color="text.secondary">
                      {tier.isFlat
                        ? `${tier.seats} seat${tier.seats > 1 ? 's' : ''} @ $${tier.subtotal.toFixed(2)} flat`
                        : `${tier.seats} seat${tier.seats > 1 ? 's' : ''} @ $${tier.pricePerSeat?.toFixed(2)}/seat`
                      }
                    </Typography>
                    {index === 0 && (
                      <Typography
                        component="span"
                        sx={classes.editLink}
                        onClick={onBack}
                      >
                        {'Edit'}
                      </Typography>
                    )}
                  </Box>
                ))}
              </>
            ) : (
              // Simple display for volume pricing or single tier
              <Box sx={classes.summaryRow}>
                <Typography variant="body1" color="text.secondary">
                  {perSeatPrice !== null
                    ? `${seatCount} seat${seatCount > 1 ? 's' : ''} @ $${perSeatPrice.toFixed(2)}/seat`
                    : `${seatCount} seat${seatCount > 1 ? 's' : ''}`
                  }
                </Typography>
                <Typography
                  component="span"
                  sx={classes.editLink}
                  onClick={onBack}
                >
                  {'Edit'}
                </Typography>
              </Box>
            )}
            {yearlySavings && billingCycle === 'year' && (
              <Box sx={classes.summaryRow}>
                <Typography variant="body1" sx={{ color: 'success.main', fontWeight: 500 }}>
                  {`Saving ${yearlySavings}% with yearly billing`}
                </Typography>
              </Box>
            )}
          </Box>
        </Grid>

        {/* Payment Details */}
        <Grid item xs={12}>
          <Typography sx={{ ...classes.formLabel, fontWeight: 600, mt: 2, mb: 1 }} variant="body1">
            {'Payment Details'}
          </Typography>
          {isTrialPlan && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1, ml: 0.5 }}>
              {'Enter your payment details to start your free trial. '}
              <strong>{'You will not be charged today.'}</strong>
            </Typography>
          )}
        </Grid>

        <Grid item xs={12}>
          <Typography sx={classes.formLabel} variant="body1">
            {'Cardholder full name'}
          </Typography>
          <FormControl
            sx={classes.newInputForm}
            variant="outlined"
          >
            <OutlinedInput
              sx={(theme) => ({
                ...classes.newFormElt,
                ...classes.checkoutInputForm
              })}
              id="cardholderName"
              margin="dense"
              name="cardholderName"
              onChange={(event) => setCardholderName(event.target.value)}
              placeholder="Cardholder full name"
              type="text"
              value={cardholderName}
            />
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <Typography sx={classes.formLabel} variant="body1">
            {'Card information'}
          </Typography>
          <StripeElementWrapper component={CardElement} id="card-elt-id" label="" />
        </Grid>

        {chargeError !== '' ? (
          <Grid sx={classes.errorLabel} item xs={12}>
            {chargeError}
          </Grid>
        ) : null}

        <Grid item xs={12}>
          <Button
            color="primary"
            disabled={!activePlan?.id || paymentLoading}
            sx={classes.primaryCta}
            type="submit"
            variant="contained"
            fullWidth
          >
            {paymentLoading
              ? 'Processing...'
              : isTrialPlan
                ? 'Start Free Trial'
                : "Let's go"
            }
          </Button>
        </Grid>

        {/* Fine print for trial plans */}
        {isTrialPlan && (
          <Grid item xs={12} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
              {`Cancel anytime before ${trialEndDate} and you won't be charged.`}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
              {`After your trial, you'll be billed $${totalPrice.toFixed(2)}/${billingInterval} for ${seatCount} seat${seatCount > 1 ? 's' : ''}.`}
            </Typography>
          </Grid>
        )}
      </Grid>
    </form>
  );
}

// Now the main "Subscribe" component that uses <Elements>:
function Subscribe(props: {}) {
  // We'll build classes here too for the container, etc.
  const theme = useTheme();
  const classes = createClasses({
    ...styles,
    ...signupStyles
  }, theme);
  const dispatch = useDispatch();

  const history = useHistory();

  // Redirect if user shouldn't be on this page (e.g., already subscribed)
  useOnboardingRedirect();

  // State for plans list from API
  const [plans, setPlans] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(true);
  const [loadError, setLoadError] = useState(null);

  // Selected plan state (set when user picks a plan in Step 0)
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedMonthlyPrice, setSelectedMonthlyPrice] = useState(null);
  const [selectedYearlyPrice, setSelectedYearlyPrice] = useState(null);

  const [seatCount, setSeatCount] = useState(1);
  const [billingCycle, setBillingCycle] = useState('month');
  const [currentStep, setCurrentStep] = useState(0); // 0 = Choose Plan, 1 = Configure, 2 = Review & Pay

  // Use memoized selector to avoid unnecessary rerenders
  const user = useSelector(selectUser);

  // Derived trial info from selected product
  const trialDays = getTrialDays(selectedProduct);
  const trialEndDate = formatTrialEndDate(selectedProduct);
  const isTrialPlan = hasTrial(selectedProduct);

  // Check if we have a selected plan for Steps 1 & 2
  const hasSelectedPlan = selectedProduct && (selectedMonthlyPrice || selectedYearlyPrice);

  // Load plans from API
  useEffect(() => {
    const loadPlans = async () => {
      if (!user) {
        log('[Subscribe] Waiting for user data...');
        return;
      }

      setLoadingPlans(true);
      setLoadError(null);

      try {
        const response = await SPBackend.getPlans(user);
        const { plans: loadedPlans } = response.data;

        log('[Subscribe] Loaded plans:', loadedPlans);

        if (!loadedPlans || loadedPlans.length === 0) {
          setLoadError('No subscription plans available. Please contact support.');
          setLoadingPlans(false);
          return;
        }

        setPlans(loadedPlans);
        setLoadingPlans(false);
      } catch (err) {
        log('[Subscribe] Error fetching plans:', err);
        setLoadError('Failed to load subscription plans. Please try again.');
        setLoadingPlans(false);
      }
    };

    loadPlans();
  }, [user?.uid]);

  // Handle plan selection from PlanPickerStep
  const handleSelectPlan = (product: Object, monthlyPrice: ?Object, yearlyPrice: ?Object, isYearly: boolean) => {
    log('[Subscribe] Plan selected:', product.name);
    setSelectedProduct(product);

    // Normalize prices based on recurring.interval (PlanPickerStep already normalizes,
    // but double-check here for safety in case raw prices are passed)
    const normalizedMonthly = monthlyPrice?.recurring?.interval === 'year' ? yearlyPrice : monthlyPrice;
    const normalizedYearly = yearlyPrice?.recurring?.interval === 'month' ? monthlyPrice : yearlyPrice;
    setSelectedMonthlyPrice(normalizedMonthly);
    setSelectedYearlyPrice(normalizedYearly);

    // Set billing cycle based on user's toggle preference from plan picker
    if (normalizedMonthly && normalizedYearly) {
      setBillingCycle(isYearly ? 'year' : 'month');
    } else if (normalizedMonthly && !normalizedYearly) {
      setBillingCycle('month');
    } else if (normalizedYearly && !normalizedMonthly) {
      setBillingCycle('year');
    }

    setCurrentStep(1);
    adTracking.trackEvent('InitiateCheckout', { content_name: product.name });
    adTracking.trackGoogleConversion('InitiateCheckout');
  };

  // Get the active plan based on billing cycle
  const activePlan = billingCycle === 'year' ? selectedYearlyPrice : selectedMonthlyPrice;

  // Calculate display price for the summary section
  const getDisplayPriceValue = () => {
    return getEffectiveTotalPrice(activePlan, seatCount);
  };

  // Get per-seat price for display (returns null for flat-amount tiers)
  const getDisplayPerSeatPrice = (): ?number => {
    if (!activePlan) return null;
    if (activePlan.billing_scheme === 'tiered' && activePlan.tiers) {
      return getCurrentTierPrice(activePlan.tiers, seatCount);
    }
    const amount = activePlan.unit_amount || activePlan.amount || 0;
    return amount / 100;
  };

  // Calculate yearly savings percentage (dynamically based on seat count)
  const yearlySavings = calculateYearlySavings(selectedMonthlyPrice, selectedYearlyPrice, seatCount);

  return (
    <Box>
      <BasicHeader title="Subscribe" />
      <ContentContainer hasSubheader={false}>
        <Grid container sx={currentStep === 0 ? { ...classes.contentCtr, maxWidth: 1000 } : classes.contentCtr}>
          {loadingPlans ? (
            <Grid item xs={12} sx={{ textAlign: 'center', py: 4 }}>
              <CircularProgress />
              <Typography variant="h5" sx={{ mt: 2 }}>{'Loading subscription plans...'}</Typography>
            </Grid>
          ) : loadError ? (
            <Grid item xs={12} sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="h5" color="error">{loadError}</Typography>
              <Button
                variant="contained"
                sx={{ mt: 2 }}
                onClick={() => history.push('/')}
              >
                {'Go Home'}
              </Button>
            </Grid>
          ) : (
            <Grid item xs={12}>
              <Box sx={{ textAlign: 'center', mb: currentStep === 0 ? 3 : 0 }}>
                <a href="/">
                  <img height={40} src={Logo} alt="AskNeo Logo" />
                </a>
                <Stepper
                  activeStep={currentStep}
                  sx={classes.checkoutStepper}
                  alternativeLabel
                >
                  {STEP_LABELS.map((label, index) => (
                    <Step key={label} completed={index < currentStep}>
                      <StepButton
                        onClick={() => {
                          if (index < currentStep) setCurrentStep(index);
                        }}
                        disabled={index > currentStep}
                      >
                        {label}
                      </StepButton>
                    </Step>
                  ))}
                </Stepper>
                <Typography variant="body1">{'Step 3 of 5'}</Typography>
              </Box>

              {/* Step 0: Plan Picker */}
              {currentStep === 0 && (
                <PlanPickerStep
                  plans={plans}
                  onSelectPlan={handleSelectPlan}
                />
              )}

              {/* Steps 1 & 2: Configure + Review & Pay */}
              {currentStep > 0 && hasSelectedPlan && (
                <Paper sx={classes.formPaper}>
                  {/* Show header summary in Step 1 (Configure) */}
                  {currentStep === 1 && (
                    <Box sx={classes.totalCtr}>
                      <Box sx={classes.totalLeft}>
                        <Typography sx={classes.totalLabel} variant="h3">
                          {selectedProduct?.name || 'Subscription'}
                        </Typography>
                        <Typography sx={classes.totalCaption} variant="body1">
                          {activePlan?.nickname || ''}
                        </Typography>
                        <Typography sx={classes.totalCaption} variant="body1">
                          {billingCycle === 'year'
                            ? 'Billed annually'
                            : 'Billed monthly'
                          }
                        </Typography>
                      </Box>
                      <Box sx={classes.totalRight}>
                        <Typography sx={classes.totalCost} variant="h3">
                          {billingCycle === 'year'
                            ? `$${getDisplayPriceValue().toFixed(2)}/year`
                            : `$${getDisplayPriceValue().toFixed(2)}/month`
                          }
                        </Typography>
                        <Typography sx={classes.totalCaption} variant="body2">
                          {getDisplayPerSeatPrice() !== null
                            ? `${seatCount} seat${seatCount > 1 ? 's' : ''} @ $${getDisplayPerSeatPrice().toFixed(2)}/seat`
                            : `${seatCount} seat${seatCount > 1 ? 's' : ''}`
                          }
                        </Typography>
                      </Box>
                    </Box>
                  )}

                  <Box sx={classes.stripeCtr}>
                    {currentStep === 1 ? (
                      <ChooseSubscriptionStep
                        monthlyPlanInfo={selectedMonthlyPrice}
                        yearlyPlanInfo={selectedYearlyPrice}
                        productInfo={selectedProduct}
                        seatCount={seatCount}
                        billingCycle={billingCycle}
                        onSeatCountChange={setSeatCount}
                        onBillingCycleChange={setBillingCycle}
                        yearlySavings={yearlySavings}
                        onContinue={() => setCurrentStep(2)}
                        trialDays={trialDays}
                        trialEndDate={trialEndDate}
                      />
                    ) : (
                      <Elements stripe={stripePromise}>
                        <ReviewAndPayStep
                          monthlyPlanInfo={selectedMonthlyPrice}
                          yearlyPlanInfo={selectedYearlyPrice}
                          productInfo={selectedProduct}
                          seatCount={seatCount}
                          billingCycle={billingCycle}
                          yearlySavings={yearlySavings}
                          onBack={() => setCurrentStep(1)}
                          trialDays={trialDays}
                          trialEndDate={trialEndDate}
                        />
                      </Elements>
                    )}
                  </Box>

                </Paper>
              )}
            </Grid>
          )}
        </Grid>
      </ContentContainer>
    </Box>
  );
}

// Finally, we export the main component:
export default Subscribe;
